import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  Check,
  ShieldCheck,
  Lock,
  FileCheck2,
  KeyRound,
  ScrollText,
  Cpu,
  Cloud,
  Server,
  Database,
  Stethoscope,
  Brain,
  ScanLine,
  Activity,
  TrendingDown,
  TrendingUp,
  Clock,
  Building2,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Phone,
  Mail,
  MessageCircle,
  Sparkles,
  Bone,
  Heart,
  Ribbon,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  RADAGENT_WA_LINK,
  CONTACT_PHONE,
  CONTACT_EMAIL,
} from "@/lib/constants";

export const metadata = {
  title:
    "RadAgent | AI-Powered Radiology Reporting for Indian Healthcare",

  description:
    "From Scan to Signed Report in Under 10 Minutes. Generate accurate, structured, and review-ready radiology reports with AI-powered reporting automation. Improve turnaround times, consistency, and radiologist productivity.",

  keywords: [
    "AI radiology reporting",
    "Radiology report generator",
    "Automated radiology reports",
    "Radiology AI software",
    "Structured reporting",
    "AI report drafting",
    "Medical reporting automation",
    "Diagnostic imaging workflow",
    "Radiologist productivity software",
    "Healthcare AI reporting",
  ],

  alternates: {
    canonical: "https://zeltaa.com/radagent",
  },

  openGraph: {
    title:
      "RadAgent | AI-Powered Radiology Reporting for Indian Healthcare",
    description:
      "From Scan to Signed Report in Under 10 Minutes. Generate accurate, structured, and review-ready radiology reports with AI-powered reporting automation. Improve turnaround times, consistency, and radiologist productivity.",
    url: "https://zeltaa.com/radagent",
    image: "/radagent.png",
  },
};

 const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "RadAgent",
    applicationCategory: "HealthcareApplication",
    operatingSystem: "Cloud",
    description:
      "From Scan to Signed Report in Under 10 Minutes. Generate accurate, structured, and review-ready radiology reports with AI-powered reporting automation. Improve turnaround times, consistency, and radiologist productivity.",
    url: "https://zeltaa.com/radagent",
    publisher: {
      "@type": "Organization",
      name: "Zeltaa",
      url: "https://zeltaa.com",
    },
  };

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

// ============ HERO ============
function Hero() {
  return (
    <section
      id="top"
      data-testid="ra-hero-section"
      className="relative isolate overflow-hidden bg-[#071B34] pt-32 pb-20 lg:pb-28"
    >
      <div className="hero-glow" aria-hidden="true" />
      <div className="intelligence-grid" aria-hidden="true" />
      <div className="noise-overlay" aria-hidden="true" />
      <div className="absolute top-1/2 -left-32 w-[460px] h-[460px] rounded-full bg-[#14D6C7]/10 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-20 right-0 w-[520px] h-[520px] rounded-full bg-[#2EE8FF]/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#14D6C7]/25 bg-[#14D6C7]/5 backdrop-blur-sm mb-7"
            >
              <span className="pulse-dot" />
              <span className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#14D6C7] font-manrope">
                AI Reporting Co-Pilot
              </span>
            </motion.div>

            <motion.h1
              data-testid="ra-hero-headline"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              className="text-balance font-manrope font-bold tracking-[-0.025em] text-white text-[clamp(2.2rem,5.6vw,4.4rem)] leading-[1.05] max-w-3xl"
            >
              AI-Powered Radiology Reporting{" "}
              <span className="bg-gradient-to-r from-[#14D6C7] via-[#2EE8FF] to-[#14D6C7] bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer">
                for Indian Diagnostic Centres.
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-7 max-w-xl font-poppins text-base sm:text-lg text-white/65 leading-relaxed"
            >
              Help your centre report more scans, faster — without hiring more
              radiologists. From scan to signed report in minutes.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                data-testid="ra-hero-demo-btn"
                href={RADAGENT_WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shine group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope"
              >
                Book a Demo
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
              </a>
              <a
                data-testid="ra-hero-how-btn"
                href="#how"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-semibold hover:bg-white/[0.08] hover:border-[#14D6C7]/40 transition-all duration-300 font-manrope backdrop-blur-sm"
              >
                See How It Works
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2.2} />
              </a>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/55 font-poppins"
            >
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> Live within 2 hours
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> No setup fee
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#14D6C7]" strokeWidth={3} /> No minimum commitment
              </span>
            </motion.div>
          </div>

          {/* Hero visual card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute inset-x-6 -top-6 h-10 bg-[#14D6C7]/30 blur-2xl rounded-full" aria-hidden="true" />
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-md p-6 sm:p-8 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#14D6C7]/15 rounded-full blur-3xl" />
              <div className="relative space-y-5">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-bold font-manrope">
                    <span className="pulse-dot" /> Live workflow
                  </div>
                  <span className="text-[10px] text-white/40 font-mono">ID: RX-00428</span>
                </div>
                {[
                  { step: "01", label: "Scan received", time: "00:00:02", state: "done" },
                  { step: "02", label: "AI image analysis", time: "00:00:46", state: "done" },
                  { step: "03", label: "Structured findings", time: "00:01:18", state: "done" },
                  { step: "04", label: "QA validation", time: "00:01:42", state: "done" },
                  { step: "05", label: "Tier-1 MD review", time: "00:03:22", state: "active" },
                  { step: "06", label: "Signed report", time: "—", state: "pending" },
                ].map((row) => (
                  <div key={row.step} className="flex items-center gap-3">
                    <span
                      className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-bold font-mono ${
                        row.state === "done"
                          ? "bg-[#14D6C7]/15 text-[#14D6C7] border border-[#14D6C7]/30"
                          : row.state === "active"
                          ? "bg-[#2EE8FF]/20 text-[#2EE8FF] border border-[#2EE8FF]/50 animate-pulse"
                          : "bg-white/5 text-white/30 border border-white/10"
                      }`}
                    >
                      {row.step}
                    </span>
                    <span
                      className={`flex-1 text-sm font-manrope ${
                        row.state === "pending" ? "text-white/35" : "text-white/85"
                      }`}
                    >
                      {row.label}
                    </span>
                    <span className="text-[11px] font-mono text-white/40">{row.time}</span>
                  </div>
                ))}
                <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs text-white/50 font-poppins">Avg TAT</span>
                  <span className="text-lg font-bold font-manrope text-[#14D6C7]">&lt; 10 min</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] backdrop-blur-md"
        >
          {[
            { v: "8–10×", l: "Higher throughput" },
            { v: "~5 min", l: "Per study reporting" },
            { v: "90%", l: "Workload reduction" },
            { v: "₹50", l: "Per study, all-in" },
          ].map((m, i) => (
            <div key={m.l} className="relative px-6 py-7 bg-[#071B34]/60 text-left">
              <div className="text-[#14D6C7] text-3xl font-bold font-manrope tracking-tight">{m.v}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/45 font-poppins">{m.l}</div>
              {i < 3 && (
                <span className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px bg-white/10" />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============ PROBLEM ============
function Problem() {
  return (
    <section
      data-testid="ra-problem-section"
      id="problem"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            01 — The Problem
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            India&apos;s imaging demand is growing faster than{" "}
            <span className="text-[#14D6C7]">reporting capacity.</span>
          </h2>
        </motion.div>

        {/* The reality */}
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { v: "1.4B+", l: "Population in India", icon: Building2 },
            { v: "~15,000", l: "Radiologists nationwide", icon: Stethoscope },
            { v: "1 / 100K", l: "Radiologist per people", icon: AlertTriangle },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative p-8 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/30 transition-colors"
              >
                <Icon className="w-6 h-6 text-[#14D6C7] mb-5" strokeWidth={1.5} />
                <div className="text-4xl sm:text-5xl font-bold font-manrope text-[#071B34] tracking-tight">
                  {s.v}
                </div>
                <div className="mt-1 text-sm text-[#6C7A89] font-poppins">{s.l}</div>
              </motion.div>
            );
          })}
        </div>

        {/* What it means */}
        <div className="mt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.22em] text-[#6C7A89] font-manrope font-semibold mb-6"
          >
            What this means for diagnostic centres
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { t: "Revenue leakage", d: "₹400–800 per study paid to external teleradiology", icon: TrendingDown },
              { t: "Slower TAT", d: "Reports take days instead of minutes", icon: Clock },
              { t: "Lower throughput", d: "Radiologists spend 30–45 min per study", icon: TrendingDown },
              { t: "Referral risk", d: "Doctors prefer centres with faster reporting", icon: AlertTriangle },
              { t: "Growth limit", d: "More scans require more radiologists", icon: TrendingDown },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="p-5 rounded-xl border border-[#6C7A89]/15 bg-white hover:shadow-sm transition-shadow"
                >
                  <Icon className="w-4.5 h-4.5 text-[#071B34]/70 mb-3" strokeWidth={1.6} />
                  <div className="text-sm font-semibold font-manrope text-[#071B34]">{item.t}</div>
                  <div className="mt-1.5 text-xs text-[#6C7A89] font-poppins leading-relaxed">{item.d}</div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Myth vs Reality */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-20 grid md:grid-cols-2 gap-4"
        >
          <div className="relative p-8 rounded-2xl border border-[#6C7A89]/25 bg-[#F9FBFD]">
            <div className="inline-flex items-center gap-2 mb-3">
              <XCircle className="w-4 h-4 text-[#6C7A89]" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-manrope font-bold text-[#6C7A89]">Myth</span>
            </div>
            <div className="text-xl sm:text-2xl font-manrope font-semibold text-[#6C7A89] line-through decoration-2 decoration-[#6C7A89]/40">
              The bottleneck is imaging equipment.
            </div>
          </div>
          <div className="relative p-8 rounded-2xl border-2 border-[#14D6C7] bg-[#14D6C7]/5">
            <div className="inline-flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-[#14D6C7]" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-manrope font-bold text-[#14D6C7]">Reality</span>
            </div>
            <div className="text-xl sm:text-2xl font-manrope font-semibold text-[#071B34]">
              The bottleneck is reporting capacity.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ INTRODUCING ============
function Introducing() {
  return (
    <section
      data-testid="ra-introducing-section"
      id="introducing"
      className="relative bg-[#071B34] py-24 sm:py-32 overflow-hidden"
    >
      <div className="intelligence-grid opacity-50" aria-hidden="true" />
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#14D6C7]/10 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            02 — Introducing RadAgent
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
            Meet RadAgent —{" "}
            <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
              your AI Reporting Co-Pilot.
            </span>
          </h2>
        </motion.div>

        {/* Workflow */}
        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {[
            { step: "01", title: "Scanner", desc: "CT · MRI · X-Ray", icon: ScanLine },
            { step: "02", title: "Co-Pilot", desc: "RadAgent (AI + Radiologist)", icon: Cpu, accent: true },
            { step: "03", title: "Signed Report", desc: "Final delivered", icon: FileCheck2 },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.step}
                className={`relative p-7 rounded-2xl border ${
                  s.accent
                    ? "border-[#14D6C7]/50 bg-gradient-to-br from-[#14D6C7]/10 to-transparent"
                    : "border-white/10 bg-white/[0.02]"
                } backdrop-blur-sm`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#14D6C7] font-bold font-manrope">
                    Step {s.step}
                  </span>
                  <Icon className="w-5 h-5 text-[#14D6C7]" strokeWidth={1.5} />
                </div>
                <div className="text-xl font-manrope font-semibold text-white">{s.title}</div>
                <div className="mt-1.5 text-sm text-white/55 font-poppins">{s.desc}</div>
              </div>
            );
          })}
        </div>

        {/* Split: AI vs Radiologist */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="p-8 rounded-2xl border border-[#14D6C7]/20 bg-white/[0.02] backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 mb-5">
              <Cpu className="w-5 h-5 text-[#14D6C7]" strokeWidth={1.6} />
              <span className="text-xs uppercase tracking-[0.22em] font-manrope font-bold text-[#14D6C7]">
                RadAgent automates
              </span>
            </div>
            <p className="text-sm text-white/55 font-poppins italic mb-5">AI handles preparation.</p>
            <ul className="space-y-3">
              {[
                "Image Analysis",
                "Structured Findings",
                "Quality Validation",
                "Draft Report Generation",
                "Report Formatting",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/85 font-manrope text-[15px]">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#14D6C7]/15 text-[#14D6C7]">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 mb-5">
              <Stethoscope className="w-5 h-5 text-white/90" strokeWidth={1.6} />
              <span className="text-xs uppercase tracking-[0.22em] font-manrope font-bold text-white/90">
                Radiologists remain responsible for
              </span>
            </div>
            <p className="text-sm text-white/55 font-poppins italic mb-5">Radiologists handle clinical judgement.</p>
            <ul className="space-y-3">
              {[
                "Clinical Interpretation",
                "Review & Verification",
                "Final Approval",
                "Sign-Off",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/85 font-manrope text-[15px]">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-white/80">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ HOW IT WORKS ============
function HowItWorks() {
  const steps = [
    { n: "01", t: "Scan automatically received", d: "DICOM ingestion directly from your modality or PACS." },
    { n: "02", t: "AI analyses medical images", d: "Modality-specific models detect and annotate findings." },
    { n: "03", t: "Structured findings generated", d: "Findings are organised into your reporting template." },
    { n: "04", t: "QA engine validates output", d: "An automatic quality layer cross-checks for completeness." },
    { n: "05", t: "Tier-1 MD radiologist reviews", d: "An MD radiologist reviews, edits, and clinically approves." },
    { n: "06", t: "Final signed report delivered", d: "Signed PDF returned to PACS / portal in minutes." },
  ];

  return (
    <section
      id="how"
      data-testid="ra-how-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            03 — How RadAgent works
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            End-to-end reporting in{" "}
            <span className="text-[#14D6C7]">minutes.</span>
          </h2>
        </motion.div>

        {/* Steps timeline */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className="relative p-7 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/40 hover:shadow-[0_20px_40px_-20px_rgba(20,214,199,0.25)] transition-all duration-400 group"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="text-3xl font-bold font-manrope text-[#14D6C7]/30 group-hover:text-[#14D6C7] transition-colors">
                  {s.n}
                </span>
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#14D6C7]/10 border border-[#14D6C7]/20 text-[#14D6C7]">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
                </span>
              </div>
              <h3 className="text-lg font-manrope font-semibold text-[#071B34] leading-snug">
                {s.t}
              </h3>
              <p className="mt-2 text-sm text-[#6C7A89] font-poppins leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 relative overflow-hidden rounded-2xl bg-[#071B34] p-8 sm:p-10 lg:p-12"
        >
          <div className="intelligence-grid opacity-40" aria-hidden="true" />
          <div className="absolute -top-24 -right-20 w-80 h-80 bg-[#14D6C7]/15 blur-[100px] rounded-full" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-4">
                <ShieldCheck className="w-3.5 h-3.5" />
                Our Guarantee
              </div>
              <h3 className="text-balance text-2xl sm:text-3xl lg:text-4xl font-manrope font-bold text-white leading-tight">
                On time, every time —{" "}
                <span className="text-[#14D6C7]">or it&apos;s on us.</span>
              </h3>
              <p className="mt-4 text-sm text-white/55 font-poppins leading-relaxed max-w-xl">
                Best turnaround time under 10 minutes depending on modality. If RadAgent fails
                to deliver within the estimated reporting window + 4 hours, the report is
                provided <span className="text-white font-semibold">free of charge</span>.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="flex items-center justify-center p-8 rounded-xl border border-[#14D6C7]/30 bg-[#14D6C7]/5 backdrop-blur-sm">
                <div className="text-center">
                  <div className="text-5xl sm:text-6xl font-bold font-manrope text-[#14D6C7]">
                    &lt; 10
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.22em] text-white/70 font-poppins font-semibold">
                    minutes best TAT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ OFFERING ============
function Offering() {
  const modalities = [
    { name: "Chest X-Ray", icon: Activity },
    { name: "CT Abdomen", icon: ScanLine },
    { name: "MRI Brain", icon: Brain },
    { name: "MRI Breast", icon: Ribbon },
    { name: "CT Chest", icon: Heart },
    { name: "CT Head", icon: Brain },
    { name: "MRI Spine", icon: Bone },
    { name: "MRI MSK", icon: Bone },
  ];

  return (
    <section
      id="offering"
      data-testid="ra-offering-section"
      className="relative bg-[#071B34] py-24 sm:py-32 overflow-hidden"
    >
      <div className="intelligence-grid opacity-40" aria-hidden="true" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[400px] bg-[#2EE8FF]/8 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            04 — RadAgent Offering
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
            More reporting capacity.{" "}
            <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
              Less operational cost.
            </span>
          </h2>
        </motion.div>

        {/* Before / After */}
        <div className="mt-14 grid lg:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-white/45 font-manrope font-bold mb-3">
              Traditional reporting
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-5xl sm:text-6xl font-bold font-manrope text-white/55">30–45</span>
              <span className="text-sm text-white/45 font-poppins">min / study</span>
            </div>
            <div className="mt-6 h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-white/20 to-white/10" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-8 rounded-2xl border border-[#14D6C7]/40 bg-gradient-to-br from-[#14D6C7]/10 to-transparent backdrop-blur-sm overflow-hidden"
          >
            <div className="absolute -top-16 -right-12 w-48 h-48 bg-[#14D6C7]/20 blur-3xl rounded-full" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-3">
                With RadAgent
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl sm:text-6xl font-bold font-manrope text-[#14D6C7]">~5</span>
                <span className="text-sm text-white/70 font-poppins">min / study</span>
              </div>
              <div className="mt-6 h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full w-[15%] bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF]" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="p-7 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-3">
              <TrendingDown className="w-3.5 h-3.5" /> Reporting workload
            </div>
            <div className="text-4xl sm:text-5xl font-bold font-manrope text-white">
              Up to <span className="text-[#14D6C7]">90%</span> reduction
            </div>
          </div>
          <div className="p-7 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-3">
              <TrendingUp className="w-3.5 h-3.5" /> Reporting throughput
            </div>
            <div className="text-4xl sm:text-5xl font-bold font-manrope text-white">
              <span className="text-[#14D6C7]">8–10×</span> higher
            </div>
          </div>
        </div>

        {/* Scale without hiring */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-10 p-8 rounded-2xl border border-[#14D6C7]/30 bg-[#14D6C7]/[0.04]"
        >
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-2">
                Scale without hiring
              </div>
              <h3 className="text-2xl sm:text-3xl font-manrope font-bold text-white leading-tight">
                Add modality volume,{" "}
                <span className="text-[#14D6C7]">not headcount.</span>
              </h3>
            </div>
            <p className="text-sm sm:text-base text-white/65 font-poppins leading-relaxed">
              Grow your imaging business without proportionally growing your radiologist
              roster — RadAgent absorbs the repetitive preparation work so your team
              focuses on what only they can do.
            </p>
          </div>
        </motion.div>

        {/* Modalities */}
        <div className="mt-16">
          <div className="text-xs uppercase tracking-[0.22em] text-white/45 font-manrope font-bold mb-6">
            Supported modalities
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {modalities.map((m) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.name}
                  className="group p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#14D6C7]/40 hover:bg-[#14D6C7]/[0.04] transition-all"
                >
                  <Icon className="w-5 h-5 text-[#14D6C7] mb-3" strokeWidth={1.5} />
                  <div className="text-sm font-manrope font-semibold text-white">{m.name}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/45 font-poppins">
            <Sparkles className="w-3.5 h-3.5 text-[#14D6C7]" />
            Coming soon: <span className="text-white/70 font-medium">Ultrasound</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ WHY RADAGENT ============
function WhyRadAgent() {
  const features = [
    {
      n: "01",
      icon: Cpu,
      t: "State-of-the-Art AI Infrastructure",
      bullets: [
        "Trained on 1M+ medical images",
        "Indian clinical datasets",
        "Continuous learning architecture",
        "Structured reporting engine",
      ],
    },
    {
      n: "02",
      icon: Stethoscope,
      t: "Human-In-The-Loop Validation",
      bullets: [
        "Tier-1 MD radiologist network",
        "Final clinical review on every report",
        "Radiologist remains legal author",
      ],
    },
    {
      n: "03",
      icon: TrendingUp,
      t: "98%+ Reporting Efficiency",
      bullets: [
        "Up to 90% reduction in reporting effort",
        "Draft reports in minutes",
        "Higher radiologist productivity",
        "Consistent structured reporting",
      ],
    },
    {
      n: "04",
      icon: ShieldCheck,
      t: "Security & Compliance Ready",
      bullets: [
        "DPDP Act aligned architecture",
        "AES-256 encryption",
        "TLS 1.3 secure transmission",
        "DICOM anonymisation",
        "Complete audit logs",
        "Role-based access controls",
      ],
    },
    {
      n: "05",
      icon: Building2,
      t: "Built For Indian Healthcare",
      bullets: [
        "Indian diagnostic centre workflows",
        "Indian patient datasets",
        "Reviewed by Indian radiologists",
        "Indian reporting economics",
      ],
    },
    {
      n: "06",
      icon: Clock,
      t: "<10 min Best-In-Class TAT",
      bullets: [
        "Tier-1 MD radiologist review",
        "Automatic QA layer",
        "Free if delivery exceeds TAT + 4 hrs",
      ],
    },
    {
      n: "07",
      icon: Cloud,
      t: "Flexible & Scalable Deployment",
      bullets: [
        "Cloud deployment",
        "Hybrid deployment",
        "Fully on-premise",
        "PACS integration support",
        "Go live within 2 hours",
      ],
    },
    {
      n: "08",
      icon: Database,
      t: "Complete Reporting Infrastructure",
      bullets: [
        "AI image analysis",
        "Report generation",
        "QA validation engine",
        "Review dashboard",
        "Radiologist approval workflow",
        "Multi-modality support",
      ],
    },
  ];

  return (
    <section
      id="why"
      data-testid="ra-why-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            05 — Why RadAgent
          </div>
          <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.08]">
            Built specifically for{" "}
            <span className="text-[#14D6C7]">Indian diagnostic centres.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg font-poppins text-[#6C7A89] max-w-2xl">
            Purpose-built for Indian diagnostic centres, validated on Indian imaging workflows,
            and designed to solve the reporting bottleneck at scale.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
                className="group relative p-7 rounded-2xl bg-white border border-[#6C7A89]/15 hover:border-[#14D6C7]/40 hover:shadow-[0_20px_40px_-20px_rgba(20,214,199,0.25)] transition-all duration-400"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/10 border border-[#14D6C7]/20 text-[#14D6C7]">
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.6} />
                  </span>
                  <span className="text-xs font-mono text-[#6C7A89]/60">{f.n}</span>
                </div>
                <h3 className="text-base font-manrope font-bold text-[#071B34] leading-snug mb-3">
                  {f.t}
                </h3>
                <ul className="space-y-1.5">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] font-poppins text-[#6C7A89]">
                      <Check className="w-3 h-3 mt-1 text-[#14D6C7] flex-shrink-0" strokeWidth={3} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============ SECURITY STRIP ============
function SecurityStrip() {
  const items = [
    { icon: Lock, label: "AES-256 Encryption" },
    { icon: KeyRound, label: "TLS 1.3" },
    { icon: ShieldCheck, label: "DPDP Aligned" },
    { icon: ScrollText, label: "Audit Logs" },
    { icon: FileCheck2, label: "DICOM Anonymisation" },
    { icon: Server, label: "Role-Based Access" },
  ];
  return (
    <section
      data-testid="ra-security-strip"
      className="relative bg-[#071B34] border-t border-white/5 py-14"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Enterprise-grade security
          </div>
          <ul className="flex flex-wrap gap-x-7 gap-y-3">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <li key={it.label} className="inline-flex items-center gap-2 text-sm text-white/70 font-poppins">
                  <Icon className="w-4 h-4 text-[#14D6C7]" strokeWidth={1.6} />
                  {it.label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ============ PRICING / FINAL CTA ============
function FinalCTA() {
  return (
    <section
      id="pricing"
      data-testid="ra-pricing-section"
      className="relative bg-[#F9FBFD] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-3xl bg-[#071B34] p-10 sm:p-16 lg:p-20"
        >
          <div className="intelligence-grid opacity-60" aria-hidden="true" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#14D6C7]/15 blur-[120px]" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#2EE8FF]/10 blur-[100px]" />

          <div className="relative grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
                <span className="block w-8 h-px bg-[#14D6C7]" />
                Ready to scale reporting capacity?
              </div>
              <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.08]">
                Let&apos;s get your centre{" "}
                <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
                  live in 2 hours.
                </span>
              </h2>
              <p className="mt-5 text-base sm:text-lg font-poppins text-white/60 max-w-xl">
                Helping diagnostic centres report more scans, faster — without hiring more
                radiologists.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  data-testid="ra-cta-demo-btn"
                  href={RADAGENT_WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-shine inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
                  Book a Demo on WhatsApp
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
                </a>
                <a
                  data-testid="ra-cta-call-btn"
                  href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-semibold hover:bg-white/[0.08] hover:border-[#14D6C7]/40 transition-all duration-300 font-manrope backdrop-blur-sm"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.2} />
                  {CONTACT_PHONE}
                </a>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/50 font-poppins">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1.5 link-underline">
                  <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
                </a>
              </div>
            </div>

            {/* Price card */}
            <div className="lg:col-span-5">
              <div className="relative p-8 rounded-2xl border border-[#14D6C7]/30 bg-white/[0.03] backdrop-blur-md">
                <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold mb-3">
                  Starting from
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-6xl font-bold font-manrope text-white">₹50</span>
                  <span className="text-sm text-white/55 font-poppins">/ study</span>
                </div>
                <div className="my-6 h-px bg-white/10" />
                <ul className="space-y-2.5">
                  {[
                    "No setup fee",
                    "No annual contract",
                    "No minimum commitment",
                    "Live within 2 hours",
                    "Dedicated onboarding & support",
                  ].map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-white/80 font-poppins">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#14D6C7]/15 text-[#14D6C7]">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ PAGE ============
export default function RadAgent() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "RadAgent — AI-Powered Radiology Reporting | Zeltaa";
  }, []);

  return (
    <main data-testid="radagent-landing">
      <Navbar />
      <Hero />
      <Problem />
      <Introducing />
      <HowItWorks />
      <Offering />
      <WhyRadAgent />
      <SecurityStrip />
      <FinalCTA />
      <Footer />
    </main>
  );
}
